import type { CmspChatInit } from '../../../../../../partner/widget/src/contract';
import type { WidgetTransport } from './cdnWidgetTransport';

export interface PreviewConversation {
  id: string;
  transport: WidgetTransport;
  setSignal?: (signal: AbortSignal) => void;
}

interface Session {
  id: string;
  callerId: string;
  controller: AbortController;
  conversation?: PreviewConversation;
  promise?: Promise<CmspChatInit>;
  failed: boolean;
  pending: boolean;
}

function aborted(): DOMException {
  return new DOMException('The preview conversation is no longer current.', 'AbortError');
}

function waitFor<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  signal.throwIfAborted();
  return new Promise((resolve, reject) => {
    const cancel = () => reject(signal.reason);
    signal.addEventListener('abort', cancel, { once: true });
    promise.then(value => {
      signal.removeEventListener('abort', cancel);
      resolve(value);
    }, error => {
      signal.removeEventListener('abort', cancel);
      reject(error);
    });
  });
}

// The deployed widget may retain its proposed ID. Normalize at the first-party
// boundary, before either the HTTP transport or the live coordinator sees it.
export function createPreviewConversationTransport(
  createConversation: (id: string, signal: AbortSignal) => Promise<PreviewConversation>,
): WidgetTransport {
  let current: Session | undefined;
  let retiring: PreviewConversation | undefined;
  let cleanup: Promise<void> | undefined;
  const finishCleanup = () => {
    if (!retiring) return Promise.resolve();
    if (!cleanup) {
      const conversation = retiring;
      cleanup = conversation.transport.dispose(conversation.id).then(() => {
        if (retiring === conversation) retiring = undefined;
      }).finally(() => { cleanup = undefined; });
    }
    return cleanup;
  };
  const matches = (session: Session, id?: string) =>
    !id || id === session.callerId || id === session.id;
  const check = (session: Session, signal = session.controller.signal) => {
    signal.throwIfAborted();
    if (current !== session) throw aborted();
  };
  const sessionFor = (id?: string) => {
    if (!current || !matches(current, id)) throw aborted();
    check(current);
    return current;
  };
  const ready = async (session: Session, signal: AbortSignal) => {
    let pending: Promise<CmspChatInit> | undefined;
    do {
      pending = session.promise;
      if (!pending) throw aborted();
      await waitFor(pending, signal);
      check(session, signal);
    } while (session.promise !== pending);
    if (!session.conversation) throw aborted();
    return session.conversation;
  };
  async function* frames<T>(
    id: string,
    signal: AbortSignal | undefined,
    operation: (conversation: PreviewConversation, signal: AbortSignal) => AsyncIterable<T>,
  ): AsyncGenerator<T> {
    const session = sessionFor(id);
    const lifetime = signal
      ? AbortSignal.any([signal, session.controller.signal])
      : session.controller.signal;
    const conversation = await ready(session, lifetime);
    for await (const frame of operation(conversation, lifetime)) {
      check(session, lifetime);
      yield frame;
    }
    check(session, lifetime);
  }
  async function result<T>(
    id: string | undefined,
    signal: AbortSignal | undefined,
    operation: (conversation: PreviewConversation, signal: AbortSignal) => Promise<T>,
  ): Promise<T> {
    const session = sessionFor(id);
    const lifetime = signal
      ? AbortSignal.any([signal, session.controller.signal])
      : session.controller.signal;
    const conversation = await ready(session, lifetime);
    const value = await waitFor(operation(conversation, lifetime), lifetime);
    check(session, lifetime);
    return value;
  }

  function initialize(id: string, restart = false): Promise<CmspChatInit> {
    const previous = current;
    if (previous && matches(previous, id)
      && !previous.controller.signal.aborted && previous.pending && !restart
      && previous.promise) return previous.promise;
    const retained = previous && matches(previous, id) ? previous.conversation : undefined;
    if (previous?.conversation && !retained) retiring = previous.conversation;
    previous?.controller.abort(aborted());
    if (retained && previous?.pending) retained.transport.detach(retained.id);
    const session: Session = {
      callerId: retained ? previous?.callerId ?? id : id,
      id: retained?.id ?? id,
      controller: new AbortController(),
      conversation: retained,
      failed: false,
      pending: true,
    };
    current = session;
    const operation = Promise.resolve().then(async () => {
      await finishCleanup();
      check(session);
      if (!session.conversation) {
        const conversation = await createConversation(session.id, session.controller.signal);
        check(session);
        session.conversation = conversation;
        session.id = conversation.id;
      }
      session.conversation.setSignal?.(session.controller.signal);
      const response = await session.conversation.transport.init(session.id);
      check(session);
      return response;
    });
    const timeout = setTimeout(() => session.controller.abort(
      new Error('chat_initialization_timeout'),
    ), 30_000);
    session.promise = waitFor(operation, session.controller.signal).finally(() => {
      clearTimeout(timeout);
      session.pending = false;
    });
    session.promise.catch(() => { session.failed = true; });
    return session.promise;
  }

  return {
    init: initialize,
    stream: (message, id, signal, operationId) =>
      frames(id, signal, (c, lifetime) => c.transport.stream(message, c.id, lifetime, operationId)),
    submitPrompt: (prompt, values, id, signal) =>
      frames(id, signal, (c, lifetime) => c.transport.submitPrompt(prompt, values, c.id, lifetime)),
    startLive: (id, signal) =>
      frames(id, signal, (c, lifetime) => c.transport.startLive(c.id, lifetime)),
    submitPreChatSurvey: (answers, id, signal) =>
      frames(id, signal, (c, lifetime) => c.transport.submitPreChatSurvey(answers, c.id, lifetime)),
    sendLive: (message, id, signal, operationId) =>
      result(id, signal, (c, lifetime) => c.transport.sendLive(message, c.id, lifetime, operationId)),
    fetchTranscript: (id, signal) =>
      result(id, signal, (c, lifetime) => c.transport.fetchTranscript(c.id, lifetime)),
    markRead: (id, messageId) => result(id, undefined, c => c.transport.markRead(c.id, messageId)),
    endLive: id => result(id, undefined, c => c.transport.endLive(c.id)),
    async refreshIdentity() {
      const session = current;
      if (!session || session.controller.signal.aborted) return;
      // Ownership may change while coordinator negotiation is pending. Re-negotiate
      // the known server ID instead of waiting behind that ownership failure.
      if (session.conversation && (session.pending || session.failed)) {
        await initialize(session.id, true);
        return;
      }
      const previousResponse = session.promise;
      if (!previousResponse) throw aborted();
      session.pending = true;
      const operation = Promise.resolve().then(async () => {
        const response = await waitFor(previousResponse, session.controller.signal);
        check(session);
        const conversation = session.conversation;
        if (!conversation) throw aborted();
        await conversation.transport.refreshIdentity();
        check(session);
        return response;
      });
      // Identity renegotiation is a readiness barrier too: a failed refresh must
      // block subsequent sends until the host explicitly retries it.
      session.promise = waitFor(operation, session.controller.signal)
        .finally(() => { session.pending = false; });
      session.promise.catch(() => { session.failed = true; });
      await session.promise;
    },
    getActiveConversationId: () => current?.conversation?.transport.getActiveConversationId()
      ?? (current?.conversation ? current.id : undefined),
    hasBrowserSession: () => (current?.conversation?.transport.hasBrowserSession() ?? false)
      || (retiring?.transport.hasBrowserSession() ?? false),
    detach(id) {
      if (!current || !matches(current, id)) return;
      current.controller.abort(aborted());
      current.conversation?.transport.detach(current.id);
    },
    async dispose(id) {
      if (!current || !matches(current, id)) return;
      const session = current;
      session.controller.abort(aborted());
      // Keep a failed teardown available for an explicit retry.
      await finishCleanup();
      await session.conversation?.transport.dispose(session.id);
      if (current === session) current = undefined;
    },
  };
}
